"use client";

import React, { useRef, useState, useEffect } from "react";
import NavItem from "./components/NavItem";
import ProductsDropdown from "./components/ProductsDropdown";
import MobileMenu from "./components/MobileMenu";
import { Logo } from "@/assets/icons";
import ContactUsButton from "../ui/contactButton";
import { useRouter, usePathname } from "next/navigation";
import { Menu } from "lucide-react";
import { NAV_ITEMS } from "./components/data";

const Navbar: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 180);
  };

  // Close dropdown on route change
  useEffect(() => {
    setIsDropdownOpen(false);
  }, [pathname]);

  // Close dropdown on outside click or escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownContainerRef.current &&
        !dropdownContainerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <>
      <nav className="fixed w-screen top-0 left-0 z-[100] backdrop-blur-md bg-white/70">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 py-2">
          <div className="flex items-center justify-between h-14 sm:h-16 w-full">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => router.push("/")}
            >
              <Logo width={36} className="cursor-pointer" />
              <span className="ml-2 text-lg xl:text-xl 2xl:text-[25px] font-bold text-[#373A74]">
                Trust Vector
              </span>
            </div>

            <div className="hidden lg:flex items-center justify-center gap-0.5 xl:gap-1 rounded-full bg-[#E4E4E4] px-1.5 xl:px-2 py-1.5 xl:py-2 whitespace-nowrap">
              {NAV_ITEMS.map((item) => {
                const hasDropdown = Boolean(item.children && item.children.length > 0);
                const isItemActive = hasDropdown
                  ? pathname === item.href ||
                    Boolean(
                      item.children?.some((child) => pathname.startsWith(child.href))
                    )
                  : item.href === "/"
                  ? pathname === "/"
                  : item.href
                  ? pathname.startsWith(item.href)
                  : false;

                if (hasDropdown) {
                  return (
                    <div
                      key={item.label}
                      ref={dropdownContainerRef}
                      className="relative"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <NavItem
                        {...item}
                        isActive={isItemActive}
                        hasDropdown={true}
                        isOpen={isDropdownOpen}
                        onClick={() => setIsDropdownOpen((prev) => !prev)}
                      />
                      <ProductsDropdown
                        isOpen={isDropdownOpen}
                        onClose={() => setIsDropdownOpen(false)}
                        pathname={pathname}
                        items={item.children}
                      />
                    </div>
                  );
                }

                return (
                  <div key={item.label}>
                    <NavItem {...item} isActive={isItemActive} />
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden lg:block">
                <ContactUsButton />
              </div>

              <button
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full bg-[#E4E4E4] hover:bg-[#d4d4d4] transition-colors"
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={20} className="text-[#373A74]" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </>
  );
};

export default Navbar;
